import { ApiError, apiService } from '../apiService';
import type {
  CreateRunBody,
  Decision,
  RerunBody,
  RunComparison,
  RunDetail,
  RouteMetrics,
  RoutePath,
  RunRoute,
  RunSummary,
  RuntimeConfig,
  TenantConfig,
  RunProfile,
  RunProfileUpdate,
  TenantSchedule,
  TenantScheduleUpdate,
  WorldRide,
  WorldRoute
} from '../../types/routing';

const BASE = '/api/v1';

export interface RunListFilters {
  status?: string;
  limit?: number;
}

export const listRuns = (filters: RunListFilters = {}) => {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.limit) params.set('limit', String(filters.limit));
  const query = params.toString();
  return apiService.get<RunSummary[]>(`${BASE}/runs${query ? `?${query}` : ''}`);
};

export const getRun = (runId: number | string) => apiService.get<RunDetail>(`${BASE}/runs/${runId}`);

export const getRunRoutes = (runId: number | string) =>
  apiService.get<RunRoute[]>(`${BASE}/runs/${runId}/routes`);

export const getRouteExplain = (runId: number | string, routeId: number | string) =>
  apiService.get<Decision[]>(`${BASE}/runs/${runId}/routes/${routeId}/explain`);

export const getRoutePath = (
  runId: number | string,
  routeId: number | string,
  rideId?: number | null
) =>
  apiService.get<RoutePath>(
    `${BASE}/runs/${runId}/routes/${routeId}/path${rideId == null ? '' : `?ride_id=${rideId}`}`
  );

export interface RouteMetricsFilters {
  tenant?: string;
  from: string;
  to: string;
  groupBy: 'day' | 'company';
}

export const getRouteMetrics = (filters: RouteMetricsFilters) => {
  const params = new URLSearchParams({
    from: filters.from,
    to: filters.to,
    group_by: filters.groupBy
  });
  if (filters.tenant) params.set('tenant', filters.tenant);
  return apiService.get<RouteMetrics>(`${BASE}/metrics/routes?${params.toString()}`);
};

export const createRun = (body: CreateRunBody) =>
  apiService.post<{ run_id: number }>(`${BASE}/runs`, body);

export const cancelRun = (runId: number | string) =>
  apiService.post<{ run_id: number; canceled: boolean }>(`${BASE}/runs/${runId}/cancel`);

/**
 * Re-plans a run under a configuration of its own. The body's rules go nowhere but the new run,
 * so this is not the same as a runtime override — that one would also change tonight's nightly.
 */
export const rerunRun = (runId: number | string, body: RerunBody) =>
  apiService.post<{ run_id: number; rerun_of: number }>(`${BASE}/runs/${runId}/rerun`, body);

/** Omit `to` on a rerun: the comparison defaults to the run it re-planned. */
export const getRunComparison = (runId: number | string, to?: number | string | null) =>
  apiService.get<RunComparison>(
    `${BASE}/runs/${runId}/compare${to == null || to === '' ? '' : `?to=${to}`}`
  );

const tenantQuery = (tenant: string) => `?tenant=${encodeURIComponent(tenant)}`;

/** What every world listing answers with: one page, and the total it was cut from. */
export interface Paged {
  total: number;
  offset: number;
  limit: number;
}

/** The page size the world screen asks for unless someone picks another. */
export const PAGE_SIZE = 100;

export interface PageRequest {
  offset?: number;
  limit?: number;
}

/** Rides and routes page within one New York day; omit it for every day at once. */
export interface WorldPageRequest extends PageRequest {
  day?: string;
}

const pageQuery = (page: PageRequest = {}) =>
  `&offset=${page.offset ?? 0}&limit=${page.limit ?? PAGE_SIZE}`;

const dayQuery = (day?: string) => (day ? `&day=${encodeURIComponent(day)}` : '');

/** total is what the day matches; total_all_days what it was narrowed from. */
export interface WorldRidesPage extends Paged {
  tenant: string;
  day: string | null;
  total_all_days: number;
  rides: WorldRide[];
}

export interface WorldRoutesPage extends Paged {
  tenant: string;
  day: string | null;
  total_all_days: number;
  routes: WorldRoute[];
}

export const listWorldRides = (tenant: string, page: WorldPageRequest = {}) =>
  apiService.get<WorldRidesPage>(
    `${BASE}/rides${tenantQuery(tenant)}${dayQuery(page.day)}${pageQuery(page)}`
  );

export const postWorldRides = (tenant: string, rides: unknown[]) =>
  apiService.post<{ tenant: string; accepted: number; total_rides: number }>(`${BASE}/rides`, {
    tenant,
    rides
  });

export const deleteWorldRide = (tenant: string, rideId: number) =>
  apiService.delete<{ deleted: number }>(`${BASE}/rides/${rideId}${tenantQuery(tenant)}`);

export const clearWorldRides = (tenant: string) =>
  apiService.delete<{ deleted: number }>(`${BASE}/rides${tenantQuery(tenant)}`);

export const listWorldRoutes = (tenant: string, page: WorldPageRequest = {}) =>
  apiService.get<WorldRoutesPage>(
    `${BASE}/routes${tenantQuery(tenant)}${dayQuery(page.day)}${pageQuery(page)}`
  );

export const postWorldRoutes = (tenant: string, routes: unknown[]) =>
  apiService.post<{ tenant: string; accepted: number; total_routes: number }>(`${BASE}/routes`, {
    tenant,
    routes
  });

export const deleteWorldRoute = (tenant: string, routeId: number) =>
  apiService.delete<{ deleted: number }>(`${BASE}/routes/${routeId}${tenantQuery(tenant)}`);

export const clearWorldRoutes = (tenant: string) =>
  apiService.delete<{ deleted: number }>(`${BASE}/routes${tenantQuery(tenant)}`);

export interface TenantDetail {
  tenant: string;
  created_at: string | null;
  parameter_rules: number;
  rides: number;
  routes: number;
}

export const listTenants = () =>
  apiService.get<{ tenants: string[]; details?: TenantDetail[] }>(`${BASE}/tenants`);

export interface EngineCheck {
  engine: string;
  url: string;
  reachable: boolean;
  routed: boolean;
  latency_ms: number;
  service: string | null;
}

export const checkEngine = (engine: string, url: string) =>
  apiService.post<EngineCheck>(`${BASE}/engines/check`, { engine, url });

export interface EngineStatus extends EngineCheck {
  engine_source: string;
}

export interface FleetInRoutes {
  id: number;
  name: string;
  named: boolean;
  routes?: number;
}

export const getFleetsInRoutes = (params: { fromDatetime: string; tenant: string }) =>
  apiService.get<{ tenant: string; fleets: FleetInRoutes[] }>(
    `${BASE}/fleets/in-routes?from_datetime=${encodeURIComponent(params.fromDatetime)}` +
      `&tenant=${encodeURIComponent(params.tenant)}`
  );

export const listTenantFleets = (tenant: string, page: PageRequest = {}) =>
  apiService.get<Paged & { tenant: string; fleets: FleetInRoutes[] }>(
    `${BASE}/fleets${tenantQuery(tenant)}${pageQuery(page)}`
  );

export const postTenantFleets = (tenant: string, fleets: { id: number; name: string }[]) =>
  apiService.post<{ tenant: string; accepted: number; total_fleets: number }>(`${BASE}/fleets`, {
    tenant,
    fleets
  });

export const clearTenantFleets = (tenant: string) =>
  apiService.delete<{ tenant: string; deleted: number }>(
    `${BASE}/fleets?tenant=${encodeURIComponent(tenant)}`
  );

export const deleteTenantFleet = (tenant: string, fleetId: number) =>
  apiService.delete<{ tenant: string; deleted: number }>(
    `${BASE}/fleets?tenant=${encodeURIComponent(tenant)}&fleet_id=${fleetId}`
  );

export interface TenantVehicle {
  car_id: number | null;
  seats: number | null;
  wav_seats: number | null;
  wav_car: boolean | null;
  car_type: string | null;
  plate: string | null;
  make_model: string | null;
}

export interface TenantDriver {
  id: number;
  name: string | null;
  registered: boolean;
  vehicle: TenantVehicle;
  route_ids: number[];
}

export const listTenantDrivers = (tenant: string, page: PageRequest = {}) =>
  apiService.get<Paged & { tenant: string; drivers: TenantDriver[] }>(
    `${BASE}/drivers${tenantQuery(tenant)}${pageQuery(page)}`
  );

export const postTenantDrivers = (tenant: string, drivers: unknown[]) =>
  apiService.post<{ tenant: string; accepted: number; total_drivers: number }>(`${BASE}/drivers`, {
    tenant,
    drivers
  });

export const clearTenantDrivers = (tenant: string) =>
  apiService.delete<{ tenant: string; deleted: number }>(
    `${BASE}/drivers?tenant=${encodeURIComponent(tenant)}`
  );

export const deleteTenantDriver = (tenant: string, driverId: number) =>
  apiService.delete<{ tenant: string; deleted: number }>(
    `${BASE}/drivers?tenant=${encodeURIComponent(tenant)}&driver_id=${driverId}`
  );

export interface CompanyInRides {
  id: number;
  name: string;
  named: boolean;
  rides?: number;
}

export const getCompaniesInRides = (params: { fromDatetime: string; tenant: string }) =>
  apiService.get<{ tenant: string; companies: CompanyInRides[] }>(
    `${BASE}/companies/in-rides?from_datetime=${encodeURIComponent(params.fromDatetime)}` +
      `&tenant=${encodeURIComponent(params.tenant)}`
  );

export const listTenantCompanies = (tenant: string, page: PageRequest = {}) =>
  apiService.get<Paged & { tenant: string; companies: CompanyInRides[] }>(
    `${BASE}/companies${tenantQuery(tenant)}${pageQuery(page)}`
  );

export const postTenantCompanies = (tenant: string, companies: unknown[]) =>
  apiService.post<{ tenant: string; accepted: number; total_companies: number }>(
    `${BASE}/companies`,
    { tenant, companies }
  );

export const clearTenantCompanies = (tenant: string) =>
  apiService.delete<{ tenant: string; deleted: number }>(
    `${BASE}/companies?tenant=${encodeURIComponent(tenant)}`
  );

export const deleteTenantCompany = (tenant: string, companyId: number) =>
  apiService.delete<{ tenant: string; deleted: number }>(
    `${BASE}/companies?tenant=${encodeURIComponent(tenant)}&company_id=${companyId}`
  );

export interface ZipCode {
  code: string;
  lat: number;
  lon: number;
  /** How many of the named tenant's routes start here; 0 when no tenant was named. */
  routes: number;
}

export const listZipCodes = (tenant: string, page: PageRequest = {}) =>
  apiService.get<Paged & { zip_codes: ZipCode[] }>(
    `${BASE}/zip-codes${tenantQuery(tenant)}${pageQuery(page)}`
  );

export const postZipCodes = (zipCodes: unknown[]) =>
  apiService.post<{ accepted: number; total_zip_codes: number }>(`${BASE}/zip-codes`, {
    zip_codes: zipCodes
  });

export const deleteZipCode = (code: string) =>
  apiService.delete<{ deleted: number }>(
    `${BASE}/zip-codes?code=${encodeURIComponent(code)}`
  );

export const getEngineStatus = (tenant: string) =>
  apiService.get<EngineStatus>(`${BASE}/engines/status?tenant=${encodeURIComponent(tenant)}`);

export const createTenant = (tenant: string) =>
  apiService.post<{ tenant: string; created: boolean }>(`${BASE}/tenants`, { tenant });

export const deleteTenant = (tenant: string) =>
  apiService.delete<{
    tenant: string;
    deleted: boolean;
    parameter_rules: number;
    rides: number;
    routes: number;
  }>(`${BASE}/tenants/${encodeURIComponent(tenant)}`);

export const getTenantConfig = (tenant: string) =>
  apiService.get<TenantConfig>(`${BASE}/tenants/${encodeURIComponent(tenant)}/config`);

export const getTenantSchedule = (tenant: string) =>
  apiService.get<TenantSchedule>(`${BASE}/tenants/${encodeURIComponent(tenant)}/schedule`);

export const putTenantSchedule = (tenant: string, body: TenantScheduleUpdate) =>
  apiService.put<TenantSchedule>(
    `${BASE}/tenants/${encodeURIComponent(tenant)}/schedule`,
    body
  );

const profilesUrl = (tenant: string) =>
  `${BASE}/tenants/${encodeURIComponent(tenant)}/run-profiles`;

export const listRunProfiles = (tenant: string) =>
  apiService.get<{ tenant: string; profiles: RunProfile[] }>(profilesUrl(tenant));

export const createRunProfile = (tenant: string, body: RunProfileUpdate) =>
  apiService.post<RunProfile>(profilesUrl(tenant), body);

export const updateRunProfile = (tenant: string, id: number, body: RunProfileUpdate) =>
  apiService.put<RunProfile>(`${profilesUrl(tenant)}/${id}`, body);

export const deleteRunProfile = (tenant: string, id: number) =>
  apiService.delete<{ tenant: string; profile_id: number; deleted: number }>(
    `${profilesUrl(tenant)}/${id}`
  );

/** A parameter change: blank the value to delete the rule with that name, scope and date. */
export interface ParameterRuleUpdate {
  name: string;
  value: string;
  state?: string;
  broker?: string;
  vehicle_type?: string;
  service_level?: string;
  effective_from?: string;
}

/** Parameters and the engine travel together; either part alone is a valid update. */
export interface TenantConfigUpdate {
  parameters?: ParameterRuleUpdate[];
  engine?: string;
  engine_urls?: {
    osrm_url?: string;
    osrm_ny_url?: string;
    valhalla_url?: string;
    myle_url?: string;
    /** Replaced whole: omit to leave the other areas alone, send [] to drop back a layer. */
    myle_area_urls?: string[];
  };
  /** Omit a field to leave it as it is; send "" to clear it back to the deployment's. */
  callbacks?: {
    routing_started_url?: string;
    route_completed_url?: string;
    routing_finished_url?: string;
    secret?: string;
  };
}

export const putTenantConfig = (tenant: string, body: TenantConfigUpdate) =>
  apiService.put<TenantConfig>(`${BASE}/tenants/${encodeURIComponent(tenant)}/config`, body);

export const clearTenantConfig = (tenant: string) =>
  apiService.delete<{ tenant: string; deleted: number }>(
    `${BASE}/tenants/${encodeURIComponent(tenant)}/config`
  );

export const getRuntimeConfig = () => apiService.get<RuntimeConfig>(`${BASE}/config`);

export interface RuntimeConfigUpdate {
  worker_concurrency?: number;
  /** The engine budget: how many calls it is asked to serve at once. */
  max_concurrent_calls?: number;
  /** How wide the pool search casts, and how much of it reaches the ride-along simulation. */
  pool_candidate_limit?: number;
  pool_simulation_limit?: number;
  parameters?: ParameterRuleUpdate[];
  /** Which engine the next run drives. Blank clears the override. */
  engine?: string;
  /** Omit a URL to leave that engine alone; send it blank to fall back to the deployment URL. */
  engine_urls?: {
    osrm_url?: string;
    osrm_ny_url?: string;
    valhalla_url?: string;
    myle_url?: string;
    /** Replaced whole: omit to leave the other areas alone, send [] to drop back a layer. */
    myle_area_urls?: string[];
  };
  /** Omit a field to leave it as it is; send "" to clear it back to the deployment's. */
  callbacks?: {
    routing_started_url?: string;
    route_completed_url?: string;
    routing_finished_url?: string;
    secret?: string;
  };
}

export const putRuntimeConfig = (body: RuntimeConfigUpdate) =>
  apiService.put<RuntimeConfig>(`${BASE}/config`, body);

export const clearRuntimeConfig = () => apiService.delete<RuntimeConfig>(`${BASE}/config`);

export interface Health {
  status: string;
  components?: Record<string, { status: string; details?: Record<string, unknown> }>;
}

/**
 * Actuator answers a failing component with 503 and the same payload — that is a server we
 * reached that is unhealthy, so only a request that never got a response counts as unreachable.
 */
export const getHealth = async (): Promise<Health> => {
  try {
    return await apiService.get<Health>('/actuator/health');
  } catch (error) {
    if (error instanceof ApiError) {
      const body = error.body as Health | null;
      return body?.status ? body : { status: 'DOWN' };
    }
    throw error;
  }
};

export const downComponents = (health: Health): string[] =>
  Object.entries(health.components ?? {})
    .filter(([, component]) => component.status !== 'UP')
    .map(([name]) => name);
